import { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";

/**
 * Main Stage set times — only acts with announced timing.
 * Source of truth for times: src/pages/Lineup.tsx artist data.
 * Party Pupils is on The Deck, not Main Stage — intentionally not listed.
 */
const SCHEDULE = [
  {
    day: "Friday",
    date: "July 17",
    doors: "Doors open 3 PM",
    sets: [
      { time: "5:30 PM", name: "Seven Wonders", role: "Fleetwood Mac Tribute" },
      { time: "7:30 PM", name: "Pablo Cruise", role: "Yacht Rock Legends" },
      { time: "9:30 PM", name: "Yacht Rock Revue", role: "Friday Headliner" },
    ],
  },
  {
    day: "Saturday",
    date: "July 18",
    doors: "Doors open Noon",
    sets: [
      {
        time: "6:30 PM",
        name: "Ambrosia",
        role: "with Peter Beckett, Walter Egan, Elliot Lurie & John Ford Coley",
      },
      { time: "9:00 PM", name: "Three Dog Night", role: "Saturday Headliner" },
    ],
  },
];

const Schedule = () => {
  usePageSEO({
    title: "Set Times — Yacht Rock Detroit 2026 | Main Stage Schedule",
    description:
      "Main Stage set times for Yacht Rock Detroit 2026 at The Roostertail. Friday July 17: doors 3 PM — Seven Wonders, Pablo Cruise, Yacht Rock Revue. Saturday July 18: doors noon — Ambrosia, Three Dog Night.",
    keywords:
      "yacht rock detroit set times, yacht rock detroit schedule, roostertail concert schedule, three dog night set time detroit, detroit festival schedule 2026",
    ogImage: PHOTOS.STAGE.bandDramaticLights,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Set Times", url: "https://yachtrockdetroit.com/schedule" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-12 md:pb-16"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            Main Stage · July 17–18
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing text="Set Times" duration={0.5} delayMultiple={0.04} />
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="py-14 md:py-20 px-6 md:px-8">
        <div className="max-w-3xl mx-auto">
          {SCHEDULE.map((day, di) => (
            <div key={day.day} className={di > 0 ? "mt-16 md:mt-24" : ""}>
              <motion.div
                className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b-2 border-yacht-charcoal pb-4"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-4xl md:text-6xl font-bold text-yacht-charcoal uppercase tracking-tight">
                  {day.day}
                  <span className="text-yacht-charcoal/35 font-medium text-xl md:text-3xl ml-3 md:ml-4 tracking-normal">
                    {day.date}
                  </span>
                </h2>
                <p className="text-yacht-blue font-bold text-base md:text-xl whitespace-nowrap">
                  {day.doors}
                </p>
              </motion.div>

              {day.sets.map((set, i) => (
                <motion.div
                  key={set.name}
                  className="grid grid-cols-[5.5rem_1fr] md:grid-cols-[8rem_1fr] gap-x-4 md:gap-x-8 py-6 md:py-8 border-b border-yacht-charcoal/15"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                >
                  <span className="text-yacht-charcoal/50 font-semibold text-sm md:text-lg pt-1 md:pt-1.5 tabular-nums whitespace-nowrap">
                    {set.time}
                  </span>
                  <span>
                    <span className="block text-2xl md:text-4xl font-bold text-yacht-charcoal leading-tight">
                      {set.name}
                    </span>
                    <span className="block text-yacht-charcoal/50 text-sm md:text-base mt-1 leading-snug">
                      {set.role}
                    </span>
                  </span>
                </motion.div>
              ))}
            </div>
          ))}

          <motion.p
            className="text-yacht-charcoal/45 text-sm md:text-base mt-10 md:mt-12 leading-relaxed"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Main Stage only — DJs and local bands play across the property all
            day, both days. More set times will be announced. Schedule subject
            to change.
          </motion.p>

          <motion.div
            className="mt-10 md:mt-14 flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <a
              href="/lineup"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-yacht-charcoal text-yacht-charcoal font-bold text-base hover:bg-yacht-charcoal hover:text-yacht-cream transition-colors"
            >
              Explore the Full Lineup
            </a>
            <a
              href="/tickets"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
              style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
            >
              Get Tickets →
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Schedule;
